import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { presetToMatrix } from "@/components/PermsMatrix";
import { PERMS, initialUserForm } from "./usuariosConstants";

// Hook que concentra estado e mutações da página Usuários.
// Torna Usuarios.jsx uma casca fina de composição.
export function useUsuarios(currentEmpresaId) {
  const [items, setItems] = useState([]);
  const [entregadores, setEntregadores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [customPresets, setCustomPresets] = useState([]);

  const [f, setF] = useState(initialUserForm);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwdConfirm, setPwdConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [savePresetOpen, setSavePresetOpen] = useState(null); // { target, permissions }
  const [savePresetName, setSavePresetName] = useState("");

  const load = useCallback(async () => setItems((await api.get("/users")).data), []);
  const loadPresets = useCallback(async () => {
    try { setCustomPresets((await api.get("/perm-presets")).data || []); }
    catch (e) {
      console.warn("Falha ao carregar presets — usando lista vazia", e);
      setCustomPresets([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadPresets();
    api.get("/entregadores").then((r) => setEntregadores(r.data));
    api.get("/empresas").then((r) => setEmpresas(r.data)).catch((e) => {
      console.warn("Lista de empresas indisponível — form segue funcional", e?.message || e);
    });
  }, [currentEmpresaId, load, loadPresets]);

  // Helpers para alternar perms/empresas em ambos os formulários (novo e edit).
  const pickTarget = (target) => (target === "f" ? [f, setF] : [editForm, setEditForm]);

  const togglePerm = (k, target = "f") => {
    const [obj, setObj] = pickTarget(target);
    const has = (obj.permissions || []).includes(k);
    setObj({ ...obj, permissions: has ? obj.permissions.filter((x) => x !== k) : [...(obj.permissions || []), k] });
  };
  const toggleEmpresa = (id, target = "f") => {
    const [obj, setObj] = pickTarget(target);
    const list = obj.empresa_ids || [];
    setObj({ ...obj, empresa_ids: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] });
  };
  const setAllPerms = (target = "f", checkAll = true) => {
    const [obj, setObj] = pickTarget(target);
    setObj({ ...obj, permissions: checkAll ? PERMS.map((p) => p.k) : [] });
  };
  const applyPreset = (target, preset) => {
    const [obj, setObj] = pickTarget(target);
    setObj({ ...obj, permissions: [...preset.perms], perms_matrix: presetToMatrix(preset.perms) });
    toast.success(`Preset "${preset.l}" aplicado · ${preset.perms.length} permissões`);
  };

  const openSavePreset = (target) => {
    const perms = target === "f" ? (f.permissions || []) : (editForm.permissions || []);
    if (perms.length === 0) return toast.error("Marque ao menos uma permissão antes de salvar");
    setSavePresetName("");
    setSavePresetOpen({ target, permissions: perms });
  };
  const confirmSavePreset = async () => {
    const nome = (savePresetName || "").trim();
    if (!nome) return toast.error("Dê um nome ao preset");
    try {
      await api.post("/perm-presets", { nome, permissions: savePresetOpen.permissions });
      toast.success(`Preset "${nome}" salvo · ${savePresetOpen.permissions.length} permissões`);
      setSavePresetOpen(null);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar preset"); }
  };
  const deleteCustomPreset = async (p) => {
    if (!window.confirm(`Excluir o preset "${p.nome}"?`)) return;
    try {
      await api.delete(`/perm-presets/${p.id}`);
      toast.success("Preset excluído");
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };

  const add = async () => {
    if (!f.nome || !f.email || !f.senha) return toast.error("Preencha todos os campos");
    try {
      const payload = { ...f };
      if (!payload.entregador_id) delete payload.entregador_id;
      await api.post("/auth/register", payload);
      toast.success("Usuário criado");
      setF(initialUserForm);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Excluído"); load(); }
    catch (e) {
      console.error("Falha ao excluir usuário", id, e);
      toast.error(e?.response?.data?.detail || "Erro ao excluir usuário");
    }
  };
  const askDelete = (u) => setPwdConfirm({
    action: () => del(u.id),
    message: `Digite sua senha para excluir o usuário "${u.nome}":`,
  });

  const genResetLink = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-link`);
      const url = `${window.location.origin}/reset-password?token=${r.data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (err) { console.warn("clipboard bloqueado", err); }
      const exp = new Date(r.data.expires_at).toLocaleString("pt-BR");
      toast.success(`Link copiado! Válido até ${exp}. Envie ao ${u.nome} por WhatsApp/telefone.`, { duration: 8000 });
      window.prompt(`Link de recuperação para ${u.nome} (${u.email}):\nExpira em: ${exp}\n\nCopie e envie ao usuário:`, url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar link"); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      nome: u.nome, email: u.email, senha: "",
      username: u.username || "", role: u.role,
      permissions: u.permissions || [],
      perms_matrix: u.perms_matrix || {},  // <— carregar matrix existente pro dialog não abrir vazio
      entregador_id: u.entregador_id || "",
      empresa_ids: u.empresa_ids || [],
    });
  };
  const saveEdit = async () => {
    const payload = {
      nome: editForm.nome, email: editForm.email, role: editForm.role,
      permissions: editForm.permissions,
      perms_matrix: editForm.perms_matrix || {},  // <— enviar matrix editada (bug fev/2026)
      entregador_id: editForm.entregador_id || null,
      empresa_ids: editForm.empresa_ids || [],
    };
    if (editForm.senha) payload.senha = editForm.senha;
    if (editForm.username !== undefined) payload.username = editForm.username;
    try {
      await api.put(`/users/${editUser.id}`, payload);
      toast.success("Usuário atualizado");
      setEditUser(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  // Seleção em massa
  const toggleSel = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllSel = () => setSelectedIds((s) =>
    s.size === items.length && items.length > 0 ? new Set() : new Set(items.map((u) => u.id))
  );
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const r = await api.post("/users/bulk-delete", { ids });
      toast.success(`${r.data.deleted} usuário(s) excluído(s)`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return {
    // Data
    items, entregadores, empresas, customPresets,
    // New user form
    f, setF, add,
    // Edit user form
    editUser, setEditUser, editForm, setEditForm, openEdit, saveEdit,
    // Perms helpers (bind ao target "f" ou "edit")
    togglePerm, toggleEmpresa, setAllPerms, applyPreset,
    // Custom presets
    savePresetOpen, setSavePresetOpen, savePresetName, setSavePresetName,
    openSavePreset, confirmSavePreset, deleteCustomPreset,
    // Delete / bulk
    pwdConfirm, setPwdConfirm, askDelete, genResetLink,
    selectedIds, setSelectedIds, toggleSel, toggleAllSel, bulkDelete,
  };
}
